import {
  APP_INITIALIZER,
  Component,
  ComponentFactoryResolver, ErrorHandler,
  Injector,
  NgModule,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {AppComponent} from './app.component';
import {NZ_DATE_LOCALE, NZ_I18N, NzI18nService, ru_RU} from 'ng-zorro-antd/i18n';
import {ru} from 'date-fns/locale';
import {FormsModule} from '@angular/forms';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {LangSwitcherService} from './lang-switcher.service';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {ExampleModule} from './example/example.module';
import {NZ_CONFIG, NzConfig} from 'ng-zorro-antd/core/config';
import {AuthService} from './services/auth.service';
import {GuideTourService} from './services/guideTour.service';
import {AppRoutingModule} from './app-routing.module';
import {ParamInterceptor} from './api.interceptor';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import {UserIdleModule} from 'angular-user-idle';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {DeleteOutline, DownloadOutline} from '@ant-design/icons-angular/icons';
import {NzMessageService} from 'ng-zorro-antd/message';
import { EventService } from '@services/event.service';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';

const icons: any[] = [ DeleteOutline, DownloadOutline ];
@Component({
  template: `
    <ng-template #datePickerTpl>
      <span class="ant-spin-dot">
        <i nz-icon><img src="../assets/icons/Calendar/M.svg" alt="calendar-icon"/> </i>
      </span>
    </ng-template>
    <ng-template #loaderTpl>
      <span class="ant-spin-dot">
        <i nz-icon><img src="../assets/icons/loader.svg" alt="loader-icon"/> </i>
      </span>
    </ng-template>
    <ng-template #selectTpl>
      <span class="ant-spin-dot">
        <i nz-icon><img src="../assets/icons/Arrow%20Down/M.svg" alt="arrow-icon"/> </i>
      </span>
    </ng-template>
  `
})

export class GlobalTemplatesComponent {
  @ViewChild('datePickerTpl', {static: true})
  nzSuffix!: TemplateRef<void>;
  @ViewChild('loaderTpl', {static: true})
  loader!: TemplateRef<void>;
  @ViewChild('selectTpl', {static: true})
  select!: TemplateRef<void>;
}

const nzConfigFactory = (
  injector: Injector,
  resolver: ComponentFactoryResolver
): NzConfig => {
  const factory = resolver.resolveComponentFactory(GlobalTemplatesComponent);
  const {nzSuffix, loader, select} = factory.create(injector).instance;
  return {
    datePicker: {
      nzSuffixIcon: nzSuffix
    },
    spin: {
      nzIndicator: loader
    },
    select: {
      nzSuffixIcon: select
    }
  };
};

// Function to initialize app and set default language
const appInitializer = (langSwitcherService: LangSwitcherService) => {
  return (): Promise<any> => {
    const savedLang = langSwitcherService.getActiveLangFromStorage();
    return new Promise((resolve) => {
      langSwitcherService.setActiveLang(savedLang);
      resolve(true);
    });
  };
};

@NgModule({
  declarations: [
    AppComponent,
    GlobalTemplatesComponent,
  ],
  imports: [
    BrowserModule,
    ExampleModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    NzButtonModule,
    AppRoutingModule,
    NzIconModule.forRoot(icons),
    UserIdleModule.forRoot({idle: 300, timeout: 1, ping: 0}),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    })
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false, }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      deps: [LangSwitcherService, Sentry.TraceService],
      multi: true,
    },
    { // The FactoryProvider
      provide: NZ_CONFIG,
      useFactory: nzConfigFactory,
      deps: [Injector, ComponentFactoryResolver]
    },
    {provide: HTTP_INTERCEPTORS, useClass: ParamInterceptor, multi: true},
    {provide: NZ_I18N, useValue: ru_RU},
    { provide: NZ_DATE_LOCALE, useValue: ru },
    LangSwitcherService,
    AuthService,
    NzNotificationService,
    NzMessageService,
    GuideTourService,
    EventService
  ],

  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private i18n: NzI18nService) { }

  switchLanguage(): void {
    this.i18n.setDateLocale(ru);
  }
}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
